/**
 * financialStatus.ts - Fonte Única da Verdade para Status Financeiro
 * 
 * Centraliza a lógica de exibição de status para Contas a Receber e Contas a Pagar.
 * Todas as tabelas, relatórios e dashboards devem usar esta função.
 * 
 * Última atualização: 2026-02-10
 */

import { PENNY_TOLERANCE } from './financialHelpers';
import { todayLocalStr } from './dateHelpers';

export interface FinancialRecord {
  status: string;
  due_date: string;
  total_value: number;
  paid_value: number;
  /** F-FIN P2: coluna gerada do banco (total − pago − desconto) — régua única de saldo quando presente */
  balance?: number | null;
  discount_value?: number | null;
}

export type DisplayStatus = 'received' | 'paid' | 'overdue' | 'open' | 'partial' | 'cancelled' | 'renegotiated';

export interface StatusInfo {
  key: DisplayStatus;
  label: string;
  /** Badge variant for shadcn Badge component */
  variant: 'default' | 'destructive' | 'secondary' | 'outline';
  /** Custom className for Badge (when variant alone isn't enough) */
  badgeClassName: string;
  /** Text color class for use in reports/tables */
  colorClassName: string;
}

/**
 * Determina o status de exibição de um registro financeiro.
 * 
 * Regras (ordem de prioridade):
 * 1. paid / balance <= 0 → Recebido/Pago (verde)
 * 2. cancelled → Cancelada
 * 3. renegotiated → Renegociada
 * 4. pending/partial + due_date < hoje → Vencida (vermelho)
 * 5. partial + due_date >= hoje → Parcial (azul)
 * 6. pending + due_date >= hoje → A Vencer (amarelo)
 *
 * F-FIN P2 (§I.147-quinquagesies): saldo = record.balance (coluna gerada total − pago − desconto)
 * ou, na ausência, total − pago − desconto. 'overdue' não é status gravado: vencido = due_date < hoje.
 * Comparação de datas usa strings yyyy-MM-dd em America/Sao_Paulo (todayLocalStr).
 */
export function getFinancialStatus(record: FinancialRecord, context: 'receivable' | 'payable' = 'receivable'): StatusInfo {
  const rawBalance =
    record.balance ?? (record.total_value - (record.paid_value || 0) - (record.discount_value || 0));
  const balance = Math.abs(rawBalance) <= PENNY_TOLERANCE ? 0 : rawBalance;
  const todayStr = todayLocalStr();
  // Normalizar due_date para yyyy-MM-dd (pegar apenas os 10 primeiros caracteres)
  const dueDateStr = record.due_date?.substring(0, 10) || '';
  const isOverdue = dueDateStr < todayStr;

  // 1. Pago / Recebido
  if (record.status === 'paid' || balance <= 0) {
    const label = context === 'payable' ? 'Paga' : 'Recebido';
    return {
      key: context === 'payable' ? 'paid' : 'received',
      label,
      variant: 'secondary',
      badgeClassName: 'bg-green-100 text-green-700 hover:bg-green-100 border border-green-200',
      colorClassName: 'text-emerald-600',
    };
  }

  // 2. Cancelada
  if (record.status === 'cancelled') {
    return {
      key: 'cancelled',
      label: 'Cancelada',
      variant: 'secondary',
      badgeClassName: '',
      colorClassName: 'text-muted-foreground',
    };
  }

  // 3. Renegociada
  if (record.status === 'renegotiated') {
    return {
      key: 'renegotiated',
      label: 'Renegociada',
      variant: 'secondary',
      badgeClassName: 'bg-purple-100 text-purple-700 hover:bg-purple-100 border border-purple-200',
      colorClassName: 'text-purple-600',
    };
  }

  // 4. Recebido Parcial (paid_value > 0 e balance > 0, independente de vencimento)
  if ((record.paid_value || 0) > 0 && balance > 0) {
    return {
      key: 'partial',
      label: context === 'payable' ? 'Pago Parcial' : 'Recebido Parcial',
      variant: 'secondary',
      badgeClassName: 'bg-orange-100 text-orange-700 hover:bg-orange-100 border border-orange-200',
      colorClassName: 'text-orange-600',
    };
  }

  // 5. Vencida (qualquer status não-terminal com due_date passada)
  if (isOverdue) {
    return {
      key: 'overdue',
      label: 'Vencida',
      variant: 'destructive',
      badgeClassName: '',
      colorClassName: 'text-destructive',
    };
  }

  // 5. Parcial (partial com due_date futura ou hoje)
  if (record.status === 'partial') {
    return {
      key: 'partial',
      label: 'Parcial',
      variant: 'secondary',
      badgeClassName: 'bg-blue-100 text-blue-700 hover:bg-blue-100 border border-blue-200',
      colorClassName: 'text-blue-600',
    };
  }

  // 6. A Vencer (pending com due_date futura ou hoje)
  return {
    key: 'open',
    label: 'A Vencer',
    variant: 'secondary',
    badgeClassName: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100 border border-yellow-200',
    colorClassName: 'text-amber-600',
  };
}

/**
 * Converte um status de banco de dados para label de exibição.
 * Útil para relatórios que recebem apenas o campo `status` sem `due_date`.
 * Quando possível, prefira `getFinancialStatus()` que considera a data de vencimento.
 */
export function getFinancialStatusLabel(status: string, context: 'receivable' | 'payable' = 'receivable'): string {
  const map: Record<string, string> = {
    pending: 'A Vencer',
    paid: 'Pago',
    cancelled: 'Cancelado',
    partial: context === 'payable' ? 'Pago Parcial' : 'Recebido Parcial',
    delivered: 'Entregue',
    in_progress: 'Em Andamento',
    renegotiated: 'Renegociado',
    received: 'Recebido',
  };
  return map[status] || status;
}
